/**
 * La cuenta del sitio y las membresías de un contacto, en su ficha (fase 6 de
 * las cuentas de clientes).
 *
 * Se pide aparte de la ficha: las membresías se copian de Stripe la primera vez
 * que se miran, y eso no puede frenar el resto. Sin cuenta ni membresías no
 * sale nada: casi todos los contactos no tienen ninguna de las dos.
 */
import { useCallback, useEffect, useState } from 'react'
import { IconChevronDown, IconCreditCard, IconLogout, IconShieldOff, IconStar, IconTicket, IconUserCheck } from '@tabler/icons-react'
import {
  cancelContactMembership,
  getContactAccount,
  revokeContactSessions,
  setContactAccountBlocked,
  type ContactAccount,
} from '../../../lib/api'
import { translateServerError, useI18n } from '../../../lib/i18n'
import { formatDashboardListDate } from '../../../lib/dashboardListDate'
import { useToast } from '../../Toast'
import { ConfirmDialog } from '../ConfirmDialog'

type Membership = ContactAccount['memberships'][number]

const ENDED = new Set(['canceled', 'incomplete_expired'])

function stateOf(row: Membership): 'active' | 'ending' | 'past_due' | 'paused' | 'ended' {
  if (ENDED.has(row.status)) return 'ended'
  if (row.status === 'past_due' || row.status === 'unpaid' || row.status === 'incomplete') return 'past_due'
  if (row.status === 'paused') return 'paused'
  return row.cancelAt ? 'ending' : 'active'
}

export function ContactAccountCard({ projectId, identity, token, canWrite, mode = 'account' }: {
  projectId: string
  identity: string
  token: string
  canWrite: boolean
  /**
   * `account`: la cuenta del sitio (lateral de la ficha). `memberships`: lo que
   * tiene (membresías de pago, gratis y paquetes), en su pestaña.
   */
  mode?: 'account' | 'memberships'
}) {
  const { t } = useI18n()
  const { showToast } = useToast()
  const [data, setData] = useState<ContactAccount | null>(null)
  /* Mientras llega (la primera vez copia de Stripe y tarda): un esqueleto en la pestaña. */
  const [loading, setLoading] = useState(true)
  const [menuFor, setMenuFor] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [cancelling, setCancelling] = useState<{ membership: Membership; when: 'period_end' | 'now' } | null>(null)

  const load = useCallback(() => {
    if (!identity.startsWith('email:')) { setData(null); return () => undefined }
    let alive = true
    setLoading(true)
    getContactAccount(projectId, identity, token)
      .then((result) => { if (alive) setData(result) })
      .catch(() => { if (alive) setData(null) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [projectId, identity, token])

  useEffect(() => load(), [load])

  if (!data) {
    return mode === 'memberships' && loading
      ? <div className="dash-cp-memberships-tab" aria-busy="true"><div className="dash-cp-skel" /><div className="dash-cp-skel" /></div>
      : null
  }
  const { account, memberships } = data
  const packs = data.packs ?? []
  const freeMemberships = data.freeMemberships ?? []
  if (mode === 'account' && !account) return null
  if (mode === 'memberships' && !memberships.length && !packs.length && !freeMemberships.length) {
    return <div className="dash-cp-empty"><IconStar size={18} /><p>{t('contacts.membershipsTab.empty')}</p></div>
  }

  const run = async (key: string, action: () => Promise<unknown>, done: string) => {
    setBusy(key)
    try {
      await action()
      showToast(done)
      load()
    } catch (error) {
      showToast(translateServerError(error, t('contacts.account.actionFailed')), 'error')
    } finally {
      setBusy(null)
    }
  }

  const priceLine = (row: Membership) => {
    if (!row.price) return ''
    const unit = ['day', 'week', 'month', 'year'].includes(row.interval) ? row.interval : 'month'
    return row.intervalCount > 1
      ? t('contacts.membership.everyN', { price: row.price, count: row.intervalCount, unit: t(`contacts.membership.units.${unit}`) })
      : t(`contacts.membership.per.${unit}`, { price: row.price })
  }

  const Wrapper = mode === 'memberships' ? 'div' : 'section'
  return (
    <Wrapper className={mode === 'memberships' ? 'dash-cp-memberships-tab' : 'dash-cp-card dash-cp-account'}>
      {mode === 'account' ? <div className="dash-cp-card-head">
        <h3><span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-cp-head-icon is-fog" aria-hidden="true"><IconUserCheck size={14} /></span> {t('contacts.account.cardTitle')}</h3>
      </div> : null}
      {account && mode === 'account' ? (
        <dl className="dash-cp-stats">
          <div>
            <dt>{t('contacts.account.label')}</dt>
            <dd>
              {account.status === 'blocked'
                ? <span className="dash-cp-state is-blocked"><IconShieldOff size={11} /> {t('contacts.account.blocked')}</span>
                : <span className="dash-contact-account-state is-on">{t('contacts.account.activeSince', { date: account.since ? formatDashboardListDate(account.since) : '' })}</span>}
            </dd>
          </div>
          {account.lastLoginAt ? (
            <div><dt>{t('contacts.account.lastLogin')}</dt><dd>{formatDashboardListDate(account.lastLoginAt)}</dd></div>
          ) : null}
          <div><dt>{t('contacts.account.sessions')}</dt><dd>{account.sessions}</dd></div>
          <div><dt>{t('contacts.account.marketing')}</dt><dd>{account.marketingOptIn ? t('opt.yes') : t('opt.no')}</dd></div>
        </dl>
      ) : null}

      {mode === 'memberships' && memberships.length ? (
        <ul className="dash-cp-memberships">
          {memberships.map((row) => {
            const state = stateOf(row)
            const detail = state === 'ended'
              ? (row.canceledAt ? t('contacts.membership.endedOn', { date: formatDashboardListDate(row.canceledAt) }) : '')
              : state === 'ending' && row.cancelAt
                ? t('contacts.membership.endsOn', { date: formatDashboardListDate(row.cancelAt) })
                : row.currentPeriodEnd ? t('contacts.membership.renews', { date: formatDashboardListDate(row.currentPeriodEnd) }) : ''
            return (
              <li key={row.id}>
                <div className="dash-cp-membership-head">
                  <IconCreditCard size={14} aria-hidden="true" />
                  <strong>{row.offerTitle || t('contacts.membership.fallbackTitle')}</strong>
                  <span className={`dash-cp-membership-state is-${state}`}>{t(`contacts.membership.state.${state}`)}</span>
                </div>
                {priceLine(row) || detail ? <small>{[priceLine(row), detail].filter(Boolean).join(' · ')}</small> : null}
                {/* Cancelar, a mano pero no a la vista: dentro de un menú, y
                    cada opción pide confirmación. */}
                {canWrite && state !== 'ended' ? (
                  <div className="dash-cp-membership-manage">
                    <button type="button" className="dash-cp-manage-btn" aria-haspopup="menu" aria-expanded={menuFor === row.id} disabled={busy !== null} onClick={() => setMenuFor(menuFor === row.id ? null : row.id)}>
                      {t('contacts.membership.manage')} <IconChevronDown size={13} />
                    </button>
                    {menuFor === row.id ? (
                      <div className="dash-cp-more-menu dash-cp-manage-menu" role="menu">
                        {state !== 'ending' ? (
                          <button type="button" role="menuitem" onClick={() => { setMenuFor(null); setCancelling({ membership: row, when: 'period_end' }) }}>{t('contacts.membership.cancelAtEnd')}</button>
                        ) : null}
                        <button type="button" role="menuitem" className="is-danger" onClick={() => { setMenuFor(null); setCancelling({ membership: row, when: 'now' }) }}>{t('contacts.membership.cancelNow')}</button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      ) : null}

      {mode === 'memberships' && (packs.length || freeMemberships.length) ? (
        <ul className="dash-cp-memberships">
          {packs.map((pack) => {
            const expired = pack.expiresAt ? new Date(pack.expiresAt).getTime() < Date.now() : false
            const done = expired || pack.remaining === 0
            return (
              <li key={pack.orderId}>
                <div className="dash-cp-membership-head">
                  <IconTicket size={14} aria-hidden="true" />
                  <strong>{pack.title}</strong>
                  <span className={`dash-cp-membership-state is-${done ? 'ended' : 'active'}`}>{t(done ? 'contacts.membershipsTab.packDone' : 'contacts.membership.state.active')}</span>
                </div>
                <small>{[
                  t('contacts.membershipsTab.pack'),
                  pack.remaining !== null && pack.limit !== null ? t('contacts.membershipsTab.left', { remaining: pack.remaining, limit: pack.limit }) : '',
                  pack.expiresAt ? t('contacts.membershipsTab.expires', { date: formatDashboardListDate(pack.expiresAt) }) : '',
                ].filter(Boolean).join(' · ')}</small>
              </li>
            )
          })}
          {freeMemberships.map((row) => (
            <li key={row.orderId}>
              <div className="dash-cp-membership-head">
                <IconStar size={14} aria-hidden="true" />
                <strong>{row.title}</strong>
                <span className={`dash-cp-membership-state is-${row.active ? 'active' : 'ended'}`}>{t(row.active ? 'contacts.membership.state.active' : 'contacts.membershipsTab.leftFree')}</span>
              </div>
              <small>{[t('contacts.membershipsTab.free'), row.percent ? t('contacts.membershipsTab.discount', { percent: row.percent }) : '', t('contacts.membershipsTab.since', { date: formatDashboardListDate(row.joinedAt) })].filter(Boolean).join(' · ')}</small>
            </li>
          ))}
        </ul>
      ) : null}

      {account && canWrite && mode === 'account' ? (
        <div className="dash-cp-actions dash-cp-account-actions">
          <button
            type="button"
            className="dash-secondary-btn"
            disabled={busy !== null || account.sessions === 0}
            onClick={() => { void run('sessions', () => revokeContactSessions(projectId, identity, token), t('contacts.account.sessionsClosed')) }}
          >
            <IconLogout size={14} /> {t('contacts.account.closeSessions')}
          </button>
          <button
            type="button"
            className={`dash-secondary-btn dash-cp-danger dash-cp-toggle${account.status === 'blocked' ? ' is-on' : ''}`}
            aria-pressed={account.status === 'blocked'}
            disabled={busy !== null}
            onClick={() => {
              const blocked = account.status !== 'blocked'
              void run('block', () => setContactAccountBlocked(projectId, identity, blocked, token), t(blocked ? 'contacts.account.blockedDone' : 'contacts.account.unblockedDone'))
            }}
          >
            <IconShieldOff size={14} /> {account.status === 'blocked' ? t('contacts.account.unblock') : t('contacts.account.block')}
          </button>
        </div>
      ) : null}

      {cancelling ? (
        <ConfirmDialog
          title={t(cancelling.when === 'now' ? 'contacts.membership.cancelNowTitle' : 'contacts.membership.cancelAtEndTitle')}
          tone={cancelling.when === 'now' ? 'danger' : 'default'}
          confirmLabel={t(cancelling.when === 'now' ? 'contacts.membership.cancelNow' : 'contacts.membership.cancelAtEnd')}
          message={t(cancelling.when === 'now' ? 'contacts.membership.cancelNowBody' : 'contacts.membership.cancelAtEndBody', {
            name: cancelling.membership.offerTitle || t('contacts.membership.fallbackTitle'),
            date: cancelling.membership.currentPeriodEnd ? formatDashboardListDate(cancelling.membership.currentPeriodEnd) : '',
          })}
          onConfirm={async () => {
            const { membership, when } = cancelling
            await run(`cancel:${membership.id}`, () => cancelContactMembership(projectId, identity, membership.id, when, token), t('contacts.membership.cancelled'))
            setCancelling(null)
          }}
          onCancel={() => setCancelling(null)}
        />
      ) : null}
    </Wrapper>
  )
}
